import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { usePrintBracket } from '../hooks/usePrintBracket';
import { BracketAccessContext } from '../App';
import { useDeadlines } from '../hooks/useDeadlines';
import BracketPrintView from '../components/BracketPrintView';


function buildEliminatedSet(games, slugToId = {}) {
  const eliminated = new Set();
  const makeSlug = str => (str || '').toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  (games || []).forEach(g => {
    if (g.status === 'final' && g.winner) {
      const loser = g.homeTeam?.id === g.winner ? g.awayTeam : g.homeTeam;
      if (loser?.id && loser.id !== 'tbd') {
        const loserId = String(loser.id);
        eliminated.add(loserId);
        // If slug, look up numeric ESPN ID
        if (isNaN(loserId)) {
          const numId = slugToId[loserId] || slugToId[makeSlug(loser.name || '')] || loser.espnId;
          if (numId) eliminated.add(String(numId));
        }
        // If numeric, slug is already in slugToId values — no need to reverse
        if (loser.espnId) eliminated.add(String(loser.espnId));
      }
    }
  });
  return eliminated;
}

function BracketsTable({ brackets, teamsById, onView, onPrint, eliminatedIds, gamesById, paymentConfig, espnNameCache = {} }) {
  const [sort, setSort] = React.useState({ col: 'score', dir: 'desc' });

  const SortBtn = ({ col, label, align }) => {
    const active = sort.col === col;
    const dir = active ? sort.dir : null;
    const next = !active ? 'asc' : dir === 'asc' ? 'desc' : 'asc';
    return (
      <button
        onClick={() => setSort({ col, dir: next })}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 3,
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
          color: active ? 'var(--blue)' : 'inherit',
          fontFamily: 'var(--font-condensed)', fontWeight: 700,
          fontSize: '0.82rem', letterSpacing: '1px', textTransform: 'uppercase', width: '100%',
        }}
      >
        {label}
        <span style={{ fontSize: '0.65rem', opacity: active ? 1 : 0.35 }}>
          {dir === 'asc' ? '↑' : dir === 'desc' ? '↓' : '↕'}
        </span>
      </button>
    );
  };

  const sorted = [...brackets].sort((a, b) => {
    const d = sort.dir === 'asc' ? 1 : -1;
    switch (sort.col) {
      case 'name':    return d * (a.name || '').localeCompare(b.name || '');
      case 'sleeper': return d * ((teamsById[a.sleeperTeamId]?.name || espnNameCache[a.sleeperTeamId] || '').localeCompare(teamsById[b.sleeperTeamId]?.name || espnNameCache[b.sleeperTeamId] || ''));
      case 'champ': {
        const champA = teamsById[a.picks?.['final-1']]?.name || '';
        const champB = teamsById[b.picks?.['final-1']]?.name || '';
        return d * champA.localeCompare(champB);
      }
      case 'score':   return d * ((a.score || 0) - (b.score || 0));
      default: return 0;
    }
  });

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="leaderboard-table">
        <thead>
          <tr>
            <th><SortBtn col="name" label="Bracket" /></th>
            <th><SortBtn col="sleeper" label="Sleeper" /></th>
            <th><SortBtn col="champ" label="Champion Pick" /></th>
            <th style={{ textAlign: 'right' }}><SortBtn col="score" label="Score" align="right" /></th>
            <th style={{ width: 28 }}></th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(b => {
            const sleeper = teamsById[b.sleeperTeamId]?.name || espnNameCache[b.sleeperTeamId] || b.sleeperTeamId || '—';
            const rawChampId = b.picks?.['final-1'];
            // Resolve playIn game IDs to actual team names
            const resolveTeamName = (id) => {
              if (!id) return '—';
              if (teamsById[id]) return teamsById[id].name;
              // It's a playIn game ID - look up the winner
              const pg = gamesById?.[id];
              if (pg?.winner) { const wt = teamsById[pg.winner]; if (wt) return wt.name; return pg.homeTeam?.id === pg.winner ? pg.homeTeam?.name : pg.awayTeam?.name || id; }
              // No winner yet - show game label
              if (pg) return pg.shortName || id;
              return id;
            };
            const champId = rawChampId;
            const resolvedChampId = gamesById?.[rawChampId]?.winner || rawChampId;
            const champ = resolveTeamName(rawChampId);
            return (
              <tr
                key={b.id}
                onClick={() => onView(b.id)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(75,139,190,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <td style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                  {b.name}

                </td>
                <td style={{ fontSize: '0.85rem', color: (eliminatedIds?.has(String(b.sleeperTeamId)) || eliminatedIds?.has(b.sleeperTeamId)) ? 'var(--red)' : 'var(--gray)' }}>
                  <span style={{ color: 'var(--gold)' }}>★</span>{' '}
                  <span style={{ textDecoration: (eliminatedIds?.has(String(b.sleeperTeamId)) || eliminatedIds?.has(b.sleeperTeamId)) ? 'line-through' : 'none' }}>
                    {sleeper}
                  </span>
                  {(eliminatedIds?.has(String(b.sleeperTeamId)) || eliminatedIds?.has(b.sleeperTeamId)) && <span style={{ marginLeft: 4, fontSize: '0.7rem' }}>💀</span>}
                </td>
                <td style={{ fontSize: '0.85rem', color: (eliminatedIds?.has(String(resolvedChampId)) || eliminatedIds?.has(resolvedChampId)) ? 'var(--red)' : 'var(--gray)' }}>
                  🏆{' '}
                  <span style={{ textDecoration: (eliminatedIds?.has(String(resolvedChampId)) || eliminatedIds?.has(resolvedChampId)) ? 'line-through' : 'none' }}>
                    {champ}
                  </span>
                  {(eliminatedIds?.has(String(resolvedChampId)) || eliminatedIds?.has(resolvedChampId)) && <span style={{ marginLeft: 4, fontSize: '0.7rem' }}>💀</span>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className="score-value">{b.score || 0}</span>
                </td>
                <td style={{ textAlign: 'center', width: 28 }}>
                  {paymentConfig?.enabled && (
                    b.paid
                      ? <span title="Paid" style={{ color: '#27ae60', fontSize: '1.1rem', fontWeight: 700 }}>$</span>
                      : <span title="Unpaid" style={{ color: '#e74c3c', fontSize: '1.1rem', fontWeight: 700, textDecoration: 'line-through' }}>$</span>
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-secondary btn-sm"
                    title="Print / Save PDF"
                    onClick={e => { e.stopPropagation(); onPrint(b.id, b); }}
                    style={{ fontSize: '0.8rem' }}
                  >🖨</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function DashboardPage() {
  const api = useApi();
  const { pastRegular, pastPlayIn, regularLabel, playInLabel, showRegularAlert, showPlayInAlert, loaded: deadlinesLoaded } = useDeadlines();
  const navigate = useNavigate();
  const { setHasBracket } = React.useContext(BracketAccessContext);
  const { userProfile, isAdmin } = useAuth();
  const { printData, printLoading, printError, openPrint, closePrint } = usePrintBracket();

  const [brackets, setBrackets] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [liveGames, setLiveGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teamsById, setTeamsById] = useState({});
  const [espnNameCache, setEspnNameCache] = useState({});
  const [allGames, setAllGames] = useState([]);
  const [scoringConfig, setScoringConfig] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [playInRoundName, setPlayInRoundName] = useState('First Four');
  const [announcement, setAnnouncement] = useState('');

  // Fetch payment config and announcement independently so they can't block the main load
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || '/api';
    fetch(`${API}/payment-config`)
      .then(r => r.ok ? r.json() : null)
      .then(pc => { if (pc) setPaymentConfig(pc); })
      .catch(() => {});
    fetch(`${API}/announcement`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.text) setAnnouncement(d.text); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const [b, lb, lg, t, allScores, sc, appCfg] = await Promise.all([
          api.getBrackets(),
          api.getLeaderboard(),
          api.getLiveScores(),
          api.getTeams().catch(() => ({ teams: [] })),
          api.getScores().catch(() => []),
          fetch(`${process.env.REACT_APP_API_URL || '/api'}/scoring`).then(r => r.json()).catch(() => null),
          fetch(`${process.env.REACT_APP_API_URL || '/api'}/scores/config`).then(r => r.json()).catch(() => null),
        ]);
        const bracketList = Array.isArray(b) ? b : [];
        setBrackets(bracketList);
        if (setHasBracket) setHasBracket(bracketList.length > 0);
        setLeaderboard(lb?.entries || []);
        setLiveGames(Array.isArray(lg) ? lg.filter(g => g.status === 'live' && g.round !== 'other' && !g.isOther).slice(0, 4) : []);
        const tById = {};
        (t?.teams || []).forEach(tm => {
          tById[tm.id] = tm;
          if (tm.espnId) tById[String(tm.espnId)] = tm;
        });
        // Also scan all games for team objects — covers ESPN numeric IDs not in teams collection
        (Array.isArray(allScores) ? allScores : []).forEach(g => {
          [g.homeTeam, g.awayTeam].forEach(team => {
            if (team && team.id && team.name && team.id !== 'tbd' && !String(team.id).includes('winner')) {
              if (!tById[String(team.id)]) tById[String(team.id)] = team;
            }
          });
        });
        setTeamsById(tById);
        setAllGames(Array.isArray(allScores) ? allScores : []);
        if (sc) setScoringConfig(sc);
        if (appCfg?.playInRoundName) setPlayInRoundName(appCfg.playInRoundName);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
    // Refresh live scores every 60 seconds
    const interval = setInterval(() => {
      api.getLiveScores().then(g => setLiveGames(g.filter(g => g.status === 'live' && g.round !== 'other' && !g.isOther).slice(0, 4))).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Load ESPN teams cache — builds numeric id->name AND slug->numericId maps
  const [espnSlugToId, setEspnSlugToId] = useState({});
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || '/api';
    fetch(`${API}/espn-teams`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.teams) {
          const nameCache = {};
          const slugMap = {};
          const makeSlug = str => (str || '').toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
          Object.entries(data.teams).forEach(([id, t]) => {
            nameCache[id] = t.name;
            // Map slug variants -> numeric id
            if (t.slug) slugMap[t.slug] = id;
            slugMap[makeSlug(t.name)] = id;
            if (t.shortName) slugMap[makeSlug(t.shortName)] = id;
            if (t.abbrev) slugMap[t.abbrev.toLowerCase()] = id;
          });
          setEspnNameCache(nameCache);
          setEspnSlugToId(slugMap);
        }
      })
      .catch(() => {});
  }, []);

  const hasAccess = isAdmin || brackets.length > 0 || userProfile?.isObserver;

  if (loading) return (
    <div className="loading-container">
      <div className="spinner"></div>
      <div className="loading-text">Loading your pool...</div>
    </div>
  );

  const myBest = brackets.reduce((best, b) => (!best || b.score > best.score) ? b : best, null);
  const myRank = myBest ? leaderboard.find(e => e.userId === myBest?.userId)?.rank : null;


  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h1 className="page-title">YOUR <span>POOL</span></h1>
          <p className="page-subtitle">Welcome back, {userProfile?.displayName}</p>
        </div>
        {!pastRegular && (
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/bracket/new')}>
            + New Bracket
          </button>
        )}
      </div>

      {/* Deadline banners */}
      {showRegularAlert && (
        <div className="alert alert-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.2rem' }}>🔒</span>
          <span>
            <strong>Bracket submissions are closed.</strong>
            {regularLabel && <> The deadline was {regularLabel}.</>}
            {' '}No new brackets can be entered.
          </span>
        </div>
      )}
      {showPlayInAlert && (
        <div className="alert alert-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.2rem' }}>⏰</span>
          <span>
            <strong>{playInRoundName} deadline has passed.</strong>
            {playInLabel && <> It closed {playInLabel}.</>}
            {' '}New brackets cannot include {playInRoundName} picks.
          </span>
        </div>
      )}

      {announcement && (
        <div className="alert alert-info" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.2rem' }}>📢</span>
          <span>{announcement}</span>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {/* Live Games Banner */}
      {liveGames.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span className="status-live">●</span>
            <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, letterSpacing: '1px', fontSize: '1rem' }}>
              LIVE NOW
            </span>
          </div>
          <div className="grid-4">
            {liveGames.map(game => (
              <div key={game.id} className="score-card live">
                <div style={{ fontSize: '0.7rem', color: 'var(--gray)', fontFamily: 'var(--font-condensed)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  {game.round}
                </div>
                <div className="score-teams">
                  <div className="score-team">
                    <div className="score-team-name" style={{ fontSize: '0.85rem' }}>
                      ({game.awayTeam?.seed}) {game.awayTeam?.abbrev}
                    </div>
                    <div className="score-team-score">{game.awayTeam?.score}</div>
                  </div>
                  <div className="score-separator">@</div>
                  <div className="score-team">
                    <div className="score-team-name" style={{ fontSize: '0.85rem' }}>
                      ({game.homeTeam?.seed}) {game.homeTeam?.abbrev}
                    </div>
                    <div className="score-team-score">{game.homeTeam?.score}</div>
                  </div>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--orange-light)', fontFamily: 'var(--font-condensed)', letterSpacing: '0.5px', marginTop: 4, textAlign: 'center' }}>
                  {game.isHalftime ? 'HALFTIME' : game.displayClock && game.period ? `${game.displayClock} · ${game.period === 1 ? '1ST' : game.period === 2 ? '2ND' : `OT${game.period > 2 ? game.period - 2 : ''}`}` : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid-2" style={{ marginBottom: 28 }}>
        {/* My Brackets */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">MY BRACKETS</h2>
            <span className="badge badge-orange">{brackets.length} bracket{brackets.length !== 1 ? 's' : ''} entered</span>
          </div>
          {brackets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--gray)' }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>🏀</div>
              <p style={{ fontFamily: 'var(--font-condensed)', fontSize: '1.1rem' }}>No brackets yet!</p>
              {!pastRegular ? (
                <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/bracket/new')}>
                  Create Your First Bracket
                </button>
              ) : (
                <p style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.9rem', color: 'var(--red)', marginTop: 12 }}>
                  🔒 Bracket submissions are closed.
                </p>
              )}
            </div>
          ) : (
            <>
              <BracketsTable
                brackets={brackets}
                teamsById={teamsById}
                onView={id => navigate(`/bracket/${id}`)}
                onPrint={(id, b) => openPrint(id, b)}
                eliminatedIds={buildEliminatedSet(allGames, espnSlugToId)}
                gamesById={Object.fromEntries(allGames.map(g => [g.id, g]))}
                paymentConfig={paymentConfig}
                espnNameCache={espnNameCache}
              />
              {paymentConfig?.enabled && (paymentConfig.paypalUrl || paymentConfig.venmoUsername || paymentConfig.contactEmail) && brackets.some(b => !b.paid) && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--slate)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)' }}>
                    {brackets.filter(b => !b.paid).length} unpaid bracket{brackets.filter(b => !b.paid).length !== 1 ? 's' : ''} &mdash; ${paymentConfig.entryFee} per bracket
                  </div>
                  <button className="btn btn-primary" onClick={() => navigate('/payment')} style={{ fontSize: '0.85rem' }}>
                    Pay Entry Fee
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Mini Leaderboard — only shown to users with bracket access */}
        {hasAccess && <div className="card">
          <div className="card-header">
            <h2 className="card-title">LEADERBOARD</h2>
            <Link to="/leaderboard" className="btn btn-secondary btn-sm">View All</Link>
          </div>
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Bracket</th>
                <th>Name</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.slice(0, Math.max(brackets.length, 5)).map(entry => (
                <tr key={entry.bracketId} className={`rank-${entry.rank}`}>
                  <td><span className="rank-number">#{entry.rank}</span></td>
                  <td style={{ fontWeight: 700 }}>{entry.bracketName}</td>
                  <td style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>{entry.userName}</td>
                  <td><span className="score-value">{entry.score}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </div>

      {/* Scoring Guide */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">SCORING GUIDE</h2>
        </div>
        <div className="grid-4">
          {[
            ...(scoringConfig?.roundPoints ? (() => {
              const rp = scoringConfig.roundPoints;
              return [
                { label: playInRoundName, pts: String(rp.playin ?? 1), note: `+${scoringConfig?.playInBonus ?? 1} bonus if ALL 4 correct` },
                { label: 'Round 1 / Round 2', pts: `${rp.R64 ?? 1} / ${rp.R32 ?? 2}`, note: '' },
                { label: 'Sweet 16 / Elite Eight', pts: `${rp.R16 ?? 3} / ${rp.R8 ?? 4}`, note: '' },
                { label: 'Final Four / Champ', pts: `${rp.R4 ?? 5} / ${rp.Final ?? 10}`, note: '' },
              ];
            })() : [
              { label: playInRoundName, pts: '1', note: '+1 bonus if ALL 4 correct' },
              { label: 'Round 1 / Round 2', pts: '1 / 2', note: '' },
              { label: 'Sweet 16 / Elite Eight', pts: '3 / 4', note: '' },
              { label: 'Final Four / Champ', pts: '5 / 10', note: '' },
            ]),
          ].map(row => (
            <div key={row.label} className="stat-box">
              <div className="stat-value">{row.pts}</div>
              <div className="stat-label">{row.label}</div>
              {row.note && <div style={{ fontSize: '0.75rem', color: 'var(--orange-light)', marginTop: 6 }}>{row.note}</div>}
            </div>
          ))}
        </div>
        <div className="alert alert-info" style={{ marginTop: 16 }}>
          <strong>🌟 Sleeper Bonus:</strong> Each bracket picks one team (seed 6-16) as their Sleeper.
          Every time your Sleeper wins, you earn {scoringConfig?.sleeperMode === 'custom'
            ? `${scoringConfig.sleeperCustomPts} bonus point${scoringConfig.sleeperCustomPts !== 1 ? 's' : ''}`
            : 'bonus points equal to that round\u2019s value'} — whether you picked them for that game or not.
          If you DID pick them, you earn double points for that game!
        </div>
      </div>

      {/* Print loading overlay */}
      {printLoading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
          <div className="spinner" />
          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '1rem', color: 'var(--cream)', letterSpacing: '1px' }}>Loading bracket for print...</div>
        </div>
      )}
      {printError && (
        <div className="alert alert-error" style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 999, minWidth: 300 }}>
          Print error: {printError}
        </div>
      )}
      {printData && (
        <BracketPrintView
          bracket={printData.bracket}
          games={printData.games}
          teamsById={printData.teamsById}
          wiring={printData.wiring}
          branding={printData.branding}
          espnNameCache={espnNameCache}
          scoringConfig={scoringConfig}
          playInRoundName={playInRoundName}
          onClose={closePrint}
        />
      )}
    </div>
  );
}
